import type { AudioSourceData, MediaSourceData } from '@lumen5/luminary';

import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { VoiceoverRegistry } from 'js/src/agent/registries/VoiceoverRegistry';
import { buildAudioSourceData } from 'js/src/data/clip-creator/clip-creator-utils';
import {
    convertBackgroundAssetToFlattenedAsset,
    flattenedAssetIsVideo,
    isFlattenedAssetMediaSourceDataCompatible,
    slideBackgroundModelIsVideoBackgroundModel,
} from 'js/src/data/media';

/** What a clip card plays: the uploaded recording itself, over the clip's range. */
export type ClipPlayback = { url: string; isAudio: boolean; startMs: number; endMs: number };

/** The recording behind a clip question: the file to play, and a small copy to draw frames from. */
export type ClipRecording = { url: string; isAudio: boolean; frameUrl?: string };

/** The playable file behind a clip question's recording, or null when there is none. */
export function resolveClipPlaybackUrl(mediaId: string): ClipRecording | null {
    const source = resolveClipSource(mediaId);
    if (!source) {
        return null;
    }
    if (source.isAudio) {
        const { mp3_url } = source.sourceData as AudioSourceData;
        return mp3_url ? { url: mp3_url, isAudio: true } : null;
    }
    const { preview_url, render_url, thumbnail_url } = source.sourceData as MediaSourceData;
    const url = preview_url || render_url;
    return url ? { url, isAudio: false, frameUrl: thumbnail_url || url } : null;
}

/**
 * Taking a media element off the page doesn't cancel its download in Chrome, so a recording keeps
 * streaming in the background unless its source is cleared first.
 */
export function releaseMedia(media: HTMLMediaElement) {
    media.removeAttribute('src');
    try {
        media.load();
    } catch {
        // Firefox throws an AbortError when this cancels the request.
    }
}

export function resolveClipSource(
    mediaId: string,
): { sourceData: MediaSourceData | AudioSourceData; assetUuid: string; isAudio: boolean } | null {
    const mediaData = MediaRegistry.getInstance().getItem(mediaId);
    const background = mediaData?.background;
    const videoUuid = background?.video?.uuid;
    if (background && videoUuid && slideBackgroundModelIsVideoBackgroundModel(background)) {
        const flattenedAsset = convertBackgroundAssetToFlattenedAsset(background);
        if (
            flattenedAssetIsVideo(flattenedAsset) &&
            isFlattenedAssetMediaSourceDataCompatible(flattenedAsset)
        ) {
            return { sourceData: flattenedAsset, assetUuid: videoUuid, isAudio: false };
        }
        return null;
    }

    // An audio recording (e.g. a podcast): the clip previews become voiceover
    // scene groups — the build operation branches on the source data's shape.
    const audioAsset = VoiceoverRegistry.getInstance().getItem(mediaId)?.audio;
    if (audioAsset?.uuid && audioAsset.has_voiceover) {
        return {
            sourceData: buildAudioSourceData(audioAsset),
            assetUuid: audioAsset.uuid,
            isAudio: true,
        };
    }
    return null;
}
